/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from "react";
import { BoltingData } from "../../apis/types";
import { ICellRendererParams } from "ag-grid-community";
import { DesignItProgress, DesignItTag } from "@design-it/react-library";
import { CustomCellRendererProps } from "ag-grid-react";
import { format } from "date-fns";
import { ModelConfig, ModelConfigs } from "../../pages/AnalysisPage/types";
import defaultRecommendations from "../../assets/recommendations.json";

export enum FeedbackValues {
  NONE = "NONE",
  OK = "OK",
  NOK = "NOK",
}

type CustomHeaderProps = {
  icon?: React.ReactElement;
  title: string;
};

const CustomHeader = (props: CustomHeaderProps) => {
  return (
    <div className="custom-header">
      <span className="header-icon">{props.icon} </span>
      <span className="header-title">{props.title}</span>
    </div>
  );
};

type CustomResultHeaderProps = {
  title: string;
  icon?: React.ReactElement;
};
const CustomResultsHeader = (props: CustomResultHeaderProps) => {
  return (
    <div
      className="custom-header"
      style={{
        display: "flex",
        justifyContent: "space-between", // Distribute space between title and icon
        alignItems: "left",
        width: "100%", // Ensure it takes up full width
      }}
    >
      <span className="header-title">{props.title}</span>
      <span className="header-icon">{props.icon} </span>
    </div>
  );
};

export const CustomResultsHeaderBuilder = (
  title: string,
  icon?: React.ReactElement
) => {
  return function CustomResultHeaderComponent(props: unknown) {
    return (
      <CustomResultsHeader
        {...(props as CustomResultHeaderProps)}
        title={title}
        icon={icon}
      />
    );
  };
};

const AnomalyTag = (value: string) => {
  if (value === "yes") {
    return (
      <DesignItTag
        className="ics-green-light"
        variant="error"
        icon-name="do_not_disturb_on"
        icon-position="first"
      >
        anomaly
      </DesignItTag>
    );
  }

  if (value === "no") {
    return (
      <DesignItTag
        className="ics-green-light"
        variant="success"
        icon-name="check_circle"
        icon-position="first"
      >
        no anomaly
      </DesignItTag>
    );
  }
  
  if (value === "-") {
    return (
      <DesignItTag
        className="ics-green-light"
        variant="warning-outlined"
        icon-name="info"
        icon-position="first"
      >
        no model
      </DesignItTag>
    );
  }
};

export type ResultsTableProps = {
  onArrowClick?: (row: any) => void;
  data?: BoltingData[];
  isLoading: boolean;
  className?: string;
  hideArrow?: boolean;
  rowsPerPage?: number;
  startPage?: number;
  nonMlFlow?: boolean;
  modelConfig?: ModelConfig;
};

export type RowViewTableProps = {
  onArrowClick?: (row: any) => void;
  data?: BoltingData[];
  isLoading: boolean;
  className?: string;
  hideArrow?: boolean;
  modelExists: boolean;
  nonMlFlow: boolean;
};

export type LabellingTableProps = {
  className?: string;
  onArrowClick?: (row: any) => void;
  setUnlabelledDataFound: (unlabelledDataFound: boolean) => void;
  setLoadedAllData: (loadedAllData: boolean) => void;
};

export const CustomHeaderBuilder = (
  title: string,
  icon: React.ReactElement
) => {
  return function CustomHeaderComponent(props: unknown) {
    return (
      <CustomHeader
        {...(props as CustomHeaderProps)}
        title={title}
        icon={icon}
      />
    );
  };
};

export const useIdCellRenderer = () => {
  return useCallback((props: ICellRendererParams) => {
    if (props.value !== undefined) {
      return `${props.data.productId}`;
    } else {
      return (
        <DesignItProgress
          style={{ "--md-circular-progress-size": "30px" }}
          indeterminate
        />
      );
    }
  }, []);
};

const ProcessColumn = () => ({
  field: "processId",
  headerComponent: CustomResultsHeaderBuilder("Process ID"),
  flex: 4,
  maxWidth: 400,
  cellClass: "custom-cell",
  cellRenderer: (props: CustomCellRendererProps) => {
    if (props.value !== undefined) {
      return `${props.data.processId}`;
    }
  },
});

const ProductColumn = () => (    {
  field: "productId",
  headerComponent: CustomResultsHeaderBuilder("Product ID"),
  flex: 3,
  maxWidth: 400,
  cellClass: "custom-cell",
  valueGetter: "node.id",
  cellRenderer: (props: CustomCellRendererProps) => {
    if (props.value !== undefined) {
      return `${props.data.productId}`;
    }
  },
});

const TimestampColumn = () => ({
  field: "createdDate",
  valueGetter: "node.id",
  headerComponent: CustomResultsHeaderBuilder("Time stamp"),
  cellRenderer: (props: CustomCellRendererProps) => {
    if (props.value !== undefined) {
      const date = new Date(props.data.createdDate); // Ensure correct date parsing
      if (!isNaN(date.getTime())) {
        return format(date, "dd.MM.yyyy, HH:mm:ss");
      } else {
        return "Invalid date";
      }
    }
  },
  flex: 3,
  maxWidth: 400,
  cellClass: "custom-cell",
});

const feedbackColumn = () => ({
  field: "feedback",
  headerComponent: () => "Feedback",
  flex: 3,
  maxWidth: 400,
  cellRenderer: (props: CustomCellRendererProps) => {
    if (props.value !== undefined) {
      return (
        <DesignItTag
          key={props.value}
          className="ics-green-light"
          variant={(() => {
            if (props.value === FeedbackValues.NOK) {
              return "error";
            }
            if (props.value === FeedbackValues.OK) {
              return "success";
            }
            return "surface";
          })()}
          icon-name={(() => {
            if (props.value === FeedbackValues.NOK) {
              return "do_not_disturb_on";
            }
            if (props.value === FeedbackValues.OK) {
              return "check_circle";
            }
            return "info";
          })()}
          icon-position="first"
        >
          {(() => {
            if (props.value === FeedbackValues.NOK) {
              return "anomaly";
            }
            if (props.value === FeedbackValues.OK) {
              return "no anomaly";
            }
            return "wanna check this";
          })()}
        </DesignItTag>
      );
    }
  },
  cellClass: "custom-cell",
});

const anomalyDetectedColumn = (name: string) => ({
  field: "anomalyDetected",
  headerComponent: () => name,
  flex: 3,
  maxWidth: 400,
  cellRenderer: (props: CustomCellRendererProps) => {
    return AnomalyTag(props.value);
  },
  cellClass: "custom-cell",
});

const classificationColumn = () => (    {
  field: "classification",
  headerComponent: () => "Classification",
  cellStyle: { justifyContent: "start" },
  cellRenderer: (props: CustomCellRendererProps) => {
    if(props.data?.feedback && props.data?.feedback === FeedbackValues.OK) {
      return "N/A";
    } else if (props.value && props.value !== "") {
      return props.value;
    } else {
      return '-';
    }
  },
  flex: 3,
  maxWidth: 400,
  cellClass: "custom-cell",
});

const toolResultsColumn = (name: string) => (    {
  field: "status",
  headerComponent: () => name,
  flex: 2,
  maxWidth: 400,
  cellClass: "custom-cell",
  cellStyle: { justifyContent: "start" },
});

export const getLabellingTableColDefs = (
) => {
  const colDefs: any = [
    /**
     * Since something's off with the AgGrid column definitions, we're using any[] here.
     * Need to be refactored to use ColDef[] or ColGroupDef[].
     */
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    ProcessColumn(),
    ProductColumn(),
    TimestampColumn(),
    toolResultsColumn("Tool Results"),
    anomalyDetectedColumn("AI Prediction"),
    feedbackColumn(),
    classificationColumn(),
  ];

  return colDefs;
};

export const getLabellingDetailsTableColDefs = () => {
  const colDefs: any = [
    /**
     * Since something's off with the AgGrid column definitions, we're using any[] here.
     * Need to be refactored to use ColDef[] or ColGroupDef[].
     */
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    ProcessColumn(),
    ProductColumn(),
    toolResultsColumn("Tool Result"),
    TimestampColumn(),
    feedbackColumn(),
    anomalyDetectedColumn("AI Prediction"),
    classificationColumn(),
  ];

  return colDefs;
};

export const getResultTableColDefs = (
  idCellRenderer: any,
  anomalyCellRenderer: any,
  arrowCellRenderer: any,
  hideArrow: boolean,
  nonMlFlow: boolean,
  navigationFunc?: any,
  modelConfig?: ModelConfig
) => {
  const colDefs: any[] = [
    ProcessColumn(),
    ProductColumn(),
    TimestampColumn(),
    toolResultsColumn("Tool Results"),
    anomalyDetectedColumn("AI Result"),
    {
      field: "classification_prediction",
      headerComponent: CustomResultsHeaderBuilder("AI Classification"),
      flex: 3,
      maxWidth: 400,
      cellRenderer: (props: CustomCellRendererProps) => {
        if (props.value !== undefined && props.value !== '') {
          return `${props.data.classification_prediction}`;
        }
        else {
          return "-";
        }
      },
    },
    {
      field: "classification_prediction",
      headerComponent: CustomResultsHeaderBuilder("AI Recommendation"),
      flex: 7,
      maxWidth: 400,
      wrapText: true,
      autoHeight: true, 
      cellRenderer: (props: CustomCellRendererProps) => {
        if (props.value !== undefined && modelConfig) {
          if (modelConfig?.recommendation && props.data.classification_prediction) {
            const recommendationType = props.data.classification_prediction as keyof typeof modelConfig.recommendation;
            const recommendationInfo = modelConfig.recommendation[recommendationType];
            if(recommendationInfo) {
              const recommendText = recommendationInfo['header'] as string;
              return modelConfig?.recommendation ? recommendText : "-";
            } else {
              return "-";
            }
          } else if (Object.hasOwn(defaultRecommendations, props.data.classification_prediction)) {
            return defaultRecommendations[props.data.classification_prediction as keyof typeof defaultRecommendations]['header'];
          } else {
            return "-";
          }
        }
      },
    }
  ];

  return nonMlFlow
    ? colDefs.filter(col => col.field !== "classification_prediction")
    : colDefs;
};

export const getLiveAnalysisTableColDefs = (modelConfigs: ModelConfigs) => {
  const colDefs: any = [
    {
      field: "model_name",
      headerComponent: CustomResultsHeaderBuilder("Model"),
      flex: 3,
      maxWidth: 400,
    },
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    {
      field: "process_id",
      headerComponent: CustomResultsHeaderBuilder("Process ID"),
      flex: 4,
      maxWidth: 400,
      cellClass: "custom-cell",
      cellRenderer: (props: CustomCellRendererProps) => {
        if (props.value !== undefined) {
          return `${props.data.process_id}`;
        }
      },
    },
    {
      field: "product_id",
      headerComponent: CustomResultsHeaderBuilder("Product ID"),
      flex: 3,
      maxWidth: 400,
      cellClass: "custom-cell",
      valueGetter: "node.id",
      cellRenderer: (props: CustomCellRendererProps) => {
        if (props.value !== undefined) {
          return `${props.data.product_id}`;
        }
      },
    },
    {
      field: "timestamp",
      valueGetter: "node.id",
      headerComponent: CustomResultsHeaderBuilder("Time stamp"),
      cellRenderer: (props: CustomCellRendererProps) => {
        if (props.value !== undefined) {
          const date = new Date(props.data.timestamp * 1000); // Ensure correct date parsing
          if (!isNaN(date.getTime())) {
            return format(date, "dd.MM.yyyy, HH:mm:ss");
          } else {
            return "Invalid date";
          }
        }
      },
      flex: 3,
      maxWidth: 400,
      cellClass: "custom-cell",
    },
    {
      field: "tool_result",
      headerComponent: () => "Tool",
      flex: 2,
      maxWidth: 400,
      cellClass: "custom-cell",
      cellStyle: { justifyContent: "start" },
    },
    {
      field: "anomaly_result",
      headerComponent: () => "AI Result",
      flex: 3,
      maxWidth: 400,
      cellRenderer: (props: CustomCellRendererProps) => {
        return AnomalyTag(props.value);
      },
      cellClass: "custom-cell",
    },
    {
      field: "classification_result",
      headerComponent: () => "Classification",
      cellStyle: { justifyContent: "start" },
      cellRenderer: (props: CustomCellRendererProps) => {
        if(props.data?.feedback && props.data?.feedback === FeedbackValues.OK) {
          return "N/A";
        } else if (props.value && props.value !== "") {
          return props.value;
        } else {
          return '-';
        }
      },
      flex: 3,
      maxWidth: 400,
      cellClass: "custom-cell",
    },
    {
      field: "classification_result",
      headerComponent: CustomResultsHeaderBuilder("AI Recommendation"),
      flex: 7,
      maxWidth: 400,
      wrapText: true,
      autoHeight: true, 
      cellRenderer: (props: CustomCellRendererProps) => {
        const modelConfig = modelConfigs && props.data && props.data.model
                          ? Object.values(modelConfigs).find((model: ModelConfig) => model.name === props.data.model)
                          : null;
        if (props.value === undefined) {
          return "-";
        }
        if (modelConfig?.recommendation && props.data.classification_result) {
          const recommendationType = props.data.classification_result as keyof typeof modelConfig.recommendation;
          const recommendationInfo = modelConfig.recommendation[recommendationType];
          if(recommendationInfo) {
            const recommendText = recommendationInfo['header'] as string;
            return modelConfig?.recommendation ? recommendText : "-";
          } 
          return "-";
        } else if (Object.hasOwn(defaultRecommendations, props.data.classification_result)) {
          return defaultRecommendations[props.data.classification_result as keyof typeof defaultRecommendations]['header'];
        } 
      },
    }
  ];

  return colDefs;
};